import { Autocomplete, Box, Button, Fade, Grid, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { useGlobals } from 'src/shared/contexts/appContext';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { EmbededCodePreviewModal } from 'src/shared/components/organisms/embededCodePreviewModal';
import { useWebFormArrays } from 'src/shared/hooks/useWebFormArrays';
import { FundraiserSettingsAutocomplete } from 'src/modules/fundraiser/components/fundraiserSettingsAutocomplete';
import { getAssociations } from 'src/shared/services/associationService';
import { Association } from 'src/shared/models/association/association';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';

type WebFormEmbedCodeProps = {
  webFormName: string;
};

export const WebFormEmbedCode = (props: WebFormEmbedCodeProps) => {
  const { webFormName } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const { themeTemplateArray, langTemplateArray } = useWebFormArrays();

  const isMounted = useIsMounted();
  const [displayCopyMessage, setDisplayCopyMessage] = useState<boolean>(false);
  const [template, setTemplate] = useState<SelectableItem>(themeTemplateArray[0]);
  const [language, setLanguage] = useState<SelectableItem>(langTemplateArray[0]);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [associations, setAssociations] = useState<Association[]>([]);
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);

  const fetchAssociations = useCallback(async () => {
    try {
      const res = await getAssociations();
      if (isMounted()) {
        const items = res.data.results || [];
        setAssociations(items);
        if (items.length === 1) setSelectedAssociation(items[0]);
      }
    } catch {
      // noop
    }
  }, [isMounted]);

  useEffect(() => {
    fetchAssociations();
  }, [fetchAssociations]);

  const getTemplate = () => {
    // Högtidsgåva har ingen inbäddnignskod än

    return `<!--- CODE TO EMBED --->
        <script src="https://forms.foreningssupport.se/js/embed/membersignup.js"></script>
        <div id="fs-signup-member-widget"></div>
        <script>
        MemberSignUpFormComponent({cId: "${currentUser?.custName}",  
        webForm: '${webFormName}',
        lang: '${language.value}',${
      selectedAssociation
        ? `
        districtId: '${selectedAssociation.countyAss_id}',
        associationId: '${selectedAssociation.localAss_id}',`
        : ''
    }
        theme: '${
          template.value
        }', navigateScroll: function(){ var element = document.getElementById('fs-signup-member-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-signup-member-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-signup-member-widget');
        </script>
        <!--- END // CODE TO EMBED --->`;
  };

  return (
    <>
      <Grid item xs={12} md={4}>
        <Typography variant='subtitle1' fontWeight='bold'>
          {t('webforms:embededCode')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {t('webforms:embededCodeDescription')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Box sx={{ mb: 2 }}>
          <FundraiserSettingsAutocomplete
            id='template-picker'
            label={t('fundraiser:selectTemplate')}
            options={themeTemplateArray}
            callback={(x) => setTemplate(x)}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <FundraiserSettingsAutocomplete
            id='language-picker'
            label={t('fundraiser:selectLanguage')}
            options={langTemplateArray}
            callback={(x) => setLanguage(x)}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            id='association-picker'
            options={associations}
            getOptionLabel={(option) => option.localAss || ''}
            value={selectedAssociation}
            onChange={(_, val) => setSelectedAssociation(val)}
            renderInput={(params) => <TextField {...params} label={t('webforms:defaultAssociation')} size='small' />}
            isOptionEqualToValue={(option, value) => option.localAss_id === value.localAss_id}
          />
        </Box>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <TextField
            rows={10}
            multiline
            fullWidth
            label={t('fundraiser:embedCodeLabel')}
            value={getTemplate()}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
            InputProps={{ readOnly: true }}
            sx={{ cursor: 'pointer !important' }}
          />
          <Fade in={displayCopyMessage}>
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                borderRadius: 1,
                bottom: '2px',
                left: '2px',
                right: '2px',
                top: '2px',
                p: 2,
                backgroundColor: 'rgba(241,244,245,0.9)',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '3rem', mb: 2 }} />
              <Typography variant='h5'>{t('fundraiser:codeCopiedMessage')}</Typography>
            </Box>
          </Fade>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant='outlined'
            size='medium'
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setShowPreviewModal(true)}
            disabled={!getTemplate()}
          >
            {t('fundraiser:preview')}
          </Button>
          <Button
            variant='outlined'
            size='medium'
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
          >
            {t('fundraiser:copyCode')}
          </Button>
        </Box>
      </Grid>

      {showPreviewModal && (
        <EmbededCodePreviewModal modalOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} embeddedCode={getTemplate()} />
      )}
    </>
  );
};
